import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  ViewChild,
  input,
} from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  GRID_LABELS,
  GRID_VIEW_DATA_ACTIONS,
  GRID_VIEW_DROPDOWN_OPTIONS,
  GridType,
  GridView,
  GridViewDialogData,
  GridViewDialogService,
  GridViewFacadeService,
  GridViewSelect,
} from '@data-access/grid-views';
import { NgClass } from '@angular/common';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { DropDownOptionOverflowTooltipDirective } from '@wcg/frontend-framework';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    NgClass,
    ReactiveFormsModule,
    DropDownOptionOverflowTooltipDirective,
  ],
  providers: [GridViewDialogService, GridViewFacadeService],
  selector: 'wcg-grid-view-drop-down',
  styleUrls: ['./grid-view-drop-down.component.css'],
  templateUrl: './grid-view-drop-down.component.html',
})
export class GridViewDropDownComponent {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _fb = inject(FormBuilder);
  private readonly _dialogService = inject(GridViewDialogService);
  private readonly _gridViewFacade = inject(GridViewFacadeService);
  private readonly _store = inject(Store);
  private readonly _facadeService = inject(GridViewFacadeService);

  @ViewChild('gridViewSelect') gridViewMatSelect!: MatSelect;
  readonly gridViewType = input.required<GridType>();

  dropDownForm = this._createForm();

  $totalGridViews = this._facadeService.$totalGridViews;

  viewsOptions = computed(() => {
    if (this._facadeService.$gridViewData()?.length > 0) {
      const options: GridViewSelect[] = this._facadeService
        .$gridViewData()
        .map((d) => ({
          id: d.id,
          label: d.name as string,
        }));
      if (options?.length > 0) {
        return [...GRID_VIEW_DROPDOWN_OPTIONS, ...options];
      }
    }
    return [...GRID_VIEW_DROPDOWN_OPTIONS];
  });

  constructor() {
    this._customListeners();
  }

  viewOption(index: number, item: GridViewSelect): string {
    return item.id!;
  }

  compareFn(option1: GridViewSelect, option2: GridViewSelect): boolean {
    return option1.id === option2.id;
  }

  openDialog(event: string) {
    if (event === 'Edit') {
      this._editView();
      this.gridViewMatSelect.close();
    }
  }

  private _editView(): void {
    const config: MatDialogConfig = {
      autoFocus: false,
      width: '550px',
    };

    const dialogConfig: GridViewDialogData<GridView[]> = {
      config: config,
      data: this._gridViewFacade.$gridViewData(),
      gridBtnLabel: GRID_LABELS.saveChanges,
      gridHeader: GRID_LABELS.editViewTableHeader,
      gridProps: {
        description: '',
        grid_type: this.gridViewType(),
        name: '',
      },
      viewAction: GRID_LABELS.edit,
    };
    this._dialogService.open(dialogConfig, 'Edit');
  }

  private _createForm(): FormGroup<{
    dropDown: FormControl<GridViewSelect>;
  }> {
    return this._fb.group({
      dropDown: new FormControl<GridViewSelect>(GRID_VIEW_DROPDOWN_OPTIONS[0], {
        nonNullable: true,
      }),
    });
  }

  private _customListeners(): void {
    toObservable(this.viewsOptions)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (options) => {
          if (options && options.length > 0) {
            if (
              !options.find(
                (o) => o.id === this.dropDownForm.get('dropDown')?.value!.id,
              )
            ) {
              this.dropDownForm
                .get('dropDown')
                ?.patchValue({ ...GRID_VIEW_DROPDOWN_OPTIONS[0] });
            }
          }
        },
      });

    toObservable(this._facadeService.$selectedView)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (option: GridViewSelect) => {
          if (this.dropDownForm.get('dropDown')?.value.id !== option.id) {
            this.dropDownForm.get('dropDown')?.reset(option, {
              emitEvent: false,
            });
          }
        },
      });

    this.dropDownForm
      .get('dropDown')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (value: GridViewSelect) =>
          this._store.dispatch(
            GRID_VIEW_DATA_ACTIONS.setCustomViewSelection({
              selectedView: value,
            }),
          ),
      });
  }
}
